import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  authApi,
  isTotpRequired,
  type LoginPayload,
  type SignupPayload,
} from '../features/auth/auth-api';
import { apiClient } from '../lib/api-client';
import { useAuthStore, type AuthUser } from '../stores/auth-store';

/**
 * Au boot, on tente un refresh silencieux pour récupérer l'access token
 * si l'utilisateur a une session active (cookie httpOnly).
 */
export function useAuthBootstrap() {
  const { status, setSession, clear, accessToken, user } = useAuthStore();

  useEffect(() => {
    if (status !== 'unknown') return;

    let cancelled = false;
    void (async () => {
      try {
        const { data } = await apiClient.post<{ accessToken: string; expiresIn: number }>(
          '/v1/auth/refresh',
        );
        if (cancelled) return;
        // Charger /me avec le nouveau token
        const me = await apiClient.get<AuthUser>('/v1/auth/me', {
          headers: { Authorization: `Bearer ${data.accessToken}` },
        });
        if (cancelled) return;
        setSession(data.accessToken, me.data);
      } catch {
        if (!cancelled) clear();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, setSession, clear]);

  return { status, accessToken, user };
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: (data) => {
      if (isTotpRequired(data)) return; // le composant déclenchera un second appel avec le code
      setSession(data.tokens.accessToken, data.user);
      void qc.invalidateQueries();
    },
  });
}

export function useSignup() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (payload: SignupPayload) => authApi.signup(payload),
    onSuccess: (data) => {
      setSession(data.tokens.accessToken, data.user);
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clear();
      qc.clear();
    },
  });
}

export function useMe() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    initialData: user ?? undefined,
    staleTime: 60_000,
  });
}
